import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useBranding } from '../contexts/BrandingContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import './ShareSlipModal.css';

/**
 * แชร์สลิปเงินเดือนเป็นรูปภาพ
 * - วาดด้วย Canvas 2D โดยตรง (ไม่พึ่ง html2canvas) ให้ได้ภาพคมชัด 2x และไม่ติดปัญหาฟอนต์/CORS บนมือถือ
 * - ผู้ใช้เลือกได้ว่าจะโชว์องค์ประกอบใดบ้างก่อนบันทึก
 * - บันทึก: มือถือใช้ Web Share (เลือก "บันทึกรูปภาพ" ลงแกลเลอรีได้) / อื่น ๆ ดาวน์โหลดไฟล์ PNG
 */

const thaiMonths = [
    '', 'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
];
const thaiMonthsShort = ['', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];

// ข้อความหัวสลิปตามโหมด (รายเดือน / รายปี)
function slipTitle(mode, yyyy, mm) {
    const year = parseInt(yyyy) + 543;
    return mode === 'yearly'
        ? { title: 'สรุปรายได้ประจำปี', subtitle: `พ.ศ. ${year}`, short: `สรุปรายได้ปี ${year}`, file: `salary_yearly_${year}.png` }
        : { title: 'สลิปเงินเดือน', subtitle: `${thaiMonths[parseInt(mm)]} พ.ศ. ${year}`, short: `สลิปเงินเดือน ${thaiMonths[parseInt(mm)]} ${year}`, file: `salary_slip_${year}_${String(mm).padStart(2, '0')}.png` };
}

const THEMES = [
    {
        // สะอาด มินิมอล แนว apple.com: หัวสีเทาอ่อน ตัวอักษรเข้ม accent ฟ้า
        key: 'clean', name: 'Clean (Apple)',
        from: '#fbfbfd', to: '#e9e9ee', accent: '#0071e3', soft: '#f5f5f7',
        decor: 'clean', pageBg: '#ffffff', cardBg: '#f5f5f7', cardBorder: null,
        headerText: '#1d1d1f', headerSub: '#6e6e73', titleColor: '#1d1d1f', subtitleColor: '#6e6e73',
        heroBorder: '#d2d2d7', heroBorderAlpha: 1,
        incomeColor: '#1d1d1f', incomeBg: '#f5f5f7', deductionColor: '#1d1d1f', deductionBg: '#f5f5f7',
        textColor: '#1d1d1f', mutedColor: '#6e6e73', lineColor: '#e5e5ea', swatch: 'linear-gradient(135deg,#ffffff,#d2d2d7)',
    },
    {
        // ทันสมัย เท่: ดาร์กโหมด + เส้นนีออนฟ้า/ม่วง
        key: 'neo', name: 'Neo (ทันสมัย)',
        from: '#0b1020', to: '#1b1440', accent: '#22d3ee', soft: '#0f1a33',
        decor: 'neo', pageBg: '#0a0f1e', cardBg: '#121a2e', cardBorder: '#24304d',
        headerText: '#e2e8f0', headerSub: '#94a3b8', titleColor: '#ffffff', subtitleColor: '#a5b4fc',
        heroBorder: '#22d3ee', heroBorderAlpha: 0.6,
        incomeColor: '#22d3ee', incomeBg: '#0e2233', deductionColor: '#fb7185', deductionBg: '#2a1220',
        textColor: '#e2e8f0', mutedColor: '#94a3b8', lineColor: '#24304d', footerColor: '#64748b',
        rowAlt: '#0f1729', totalLine: '#334b6e',
        swatch: 'linear-gradient(135deg,#0b1020 0%,#22d3ee 60%,#a855f7 100%)',
    },
    {
        // ธีมอั่งเปา/แต๊ะเอีย: แดงมงคล + ทอง พร้อมลวดลายเหรียญ ก้อนทอง ประกายดาว
        key: 'angpao', name: 'อั่งเปา มงคล',
        from: '#d11a1a', to: '#7a0c0c', accent: '#b91c1c', soft: '#fff4e6',
        decor: 'angpao', gold: '#f6c453', goldDark: '#b8860b',
        pageBg: '#fdf2e9', cardBg: '#fffaf3', cardBorder: '#f1d8a8', titleColor: '#ffe9a8',
        // โทนเดียวทั้งสลิป: รายรับ = ทองเข้ม, รายหัก = แดงเข้ม (ไม่มีเขียว)
        incomeColor: '#9a6b00', incomeBg: '#fff3d6', deductionColor: '#9f1239', deductionBg: '#fde4e4',
        textColor: '#5c1a1a', mutedColor: '#8b5e3c', lineColor: '#efd9b4',
    },
    {
        // โคมแดง มังกรทอง: พื้นดำแดงเข้ม โคมไฟ + ทอง (จีนแบบหรู)
        key: 'lantern', name: 'โคมแดง มังกรทอง',
        from: '#2a0707', to: '#0d0303', accent: '#c9a227', soft: '#2a1010',
        decor: 'lantern', gold: '#f6c453', goldDark: '#b8860b',
        pageBg: '#140505', cardBg: '#241010', cardBorder: '#6b4a12', titleColor: '#ffe9a8',
        headerText: '#ffe9a8', headerSub: '#d9b46a', subtitleColor: '#f6c453',
        heroBorder: '#f6c453', heroBorderAlpha: 0.9,
        incomeColor: '#f6c453', incomeBg: '#2e1d05', deductionColor: '#ff8a8a', deductionBg: '#3a0f0f',
        textColor: '#fbe9c8', mutedColor: '#c9a77a', lineColor: '#4a2a12', footerColor: '#a6875a',
        rowAlt: '#1c0b0b', totalLine: '#8a6a2a',
        swatch: 'linear-gradient(135deg,#7a0c0c 0%,#1a0505 55%,#c9a227 100%)',
    },
    {
        // ทองคำ มั่งคั่ง: หัวทองอร่าม เหรียญ-ก้อนทองเต็ม การ์ดครีม
        key: 'gold', name: 'ทองคำ มั่งคั่ง',
        from: '#e3b53a', to: '#8a5a00', accent: '#8a5a00', soft: '#fff6dc',
        decor: 'gold', gold: '#f6c453', goldDark: '#b8860b',
        pageBg: '#fffaf0', cardBg: '#fffdf7', cardBorder: '#e7cf96', titleColor: '#fff8e1',
        headerText: '#fff4cc', headerSub: '#ffe7a3', subtitleColor: '#fff1c2',
        incomeColor: '#8a5a00', incomeBg: '#fff3d6', deductionColor: '#9f1239', deductionBg: '#fde4e4',
        textColor: '#4a3208', mutedColor: '#8a6d3b', lineColor: '#efe1bd', rowAlt: '#fff6e3', totalLine: '#d9b66a',
        swatch: 'linear-gradient(135deg,#fff3c4 0%,#e3b53a 45%,#8a5a00 100%)',
    },
    {
        // ดอกเหมยบาน: แดงเข้มเลือดหมู + กิ่งเหมยดอกชมพู-ขาว สื่อถึงความเจริญรุ่งเรือง
        key: 'plum', name: 'ดอกเหมยบาน',
        from: '#b9123a', to: '#5e0a22', accent: '#b9123a', soft: '#ffeef1',
        decor: 'plum', gold: '#f6c453', goldDark: '#b8860b',
        pageBg: '#fff5f5', cardBg: '#fffafa', cardBorder: '#f3c4cc', titleColor: '#ffe4ea',
        headerText: '#ffe4ea', headerSub: '#f6b8c6', subtitleColor: '#ffd0da',
        incomeColor: '#a31545', incomeBg: '#ffe9ee', deductionColor: '#7a1b2e', deductionBg: '#fbe0e4',
        textColor: '#5a1424', mutedColor: '#9c5566', lineColor: '#f5d5db', rowAlt: '#fff0f2', totalLine: '#e5a2b0',
        swatch: 'linear-gradient(135deg,#5e0a22 0%,#b9123a 55%,#ffb3c6 100%)',
    },
    {
        // ประทัดตรุษจีน: แดงสด + พวงประทัด + พลุทอง คึกคักเฉลิมฉลอง
        key: 'firecracker', name: 'ประทัด ตรุษจีน',
        from: '#e11d2e', to: '#7f0000', accent: '#c2410c', soft: '#fff1e0',
        decor: 'firecracker', gold: '#f6c453', goldDark: '#b8860b',
        pageBg: '#fff3ec', cardBg: '#fff9f5', cardBorder: '#f5c9a8', titleColor: '#ffe9a8',
        headerText: '#ffe9a8', headerSub: '#ffd08a', subtitleColor: '#ffe0b0',
        incomeColor: '#b45309', incomeBg: '#fff1dc', deductionColor: '#9f1239', deductionBg: '#fde4e4',
        textColor: '#5c1a1a', mutedColor: '#96613c', lineColor: '#f3dcc6', rowAlt: '#fff4ea', totalLine: '#e6b88a',
        swatch: 'linear-gradient(135deg,#7f0000 0%,#e11d2e 50%,#f6c453 100%)',
    },
];

// ธีมจีนมงคลขึ้นก่อน ตามด้วยธีมทั่วไป
const THEME_LIST = [...THEMES.filter(t => t.gold), ...THEMES.filter(t => !t.gold)];
const DEFAULT_THEME_KEY = 'angpao';

// สีเริ่มต้นของธีมทั่วไป (ธีมอั่งเปา override)
const DEFAULT_PALETTE = {
    incomeColor: '#15803d', incomeBg: '#f0fdf4', deductionColor: '#b91c1c', deductionBg: '#fef2f2',
    textColor: '#0f172a', mutedColor: '#64748b', lineColor: '#e2e8f0',
    headerText: 'rgba(255,255,255,0.92)', headerSub: 'rgba(255,255,255,0.75)',
    titleColor: '#ffffff', subtitleColor: 'rgba(255,255,255,0.9)', footerColor: '#94a3b8',
};

// ---------- ลวดลายธีมโคมแดง ----------
function drawLantern(ctx, x, y, w, alpha = 1) {
    const h = w * 1.25;
    ctx.save();
    ctx.globalAlpha = alpha;
    // เชือกแขวน
    ctx.strokeStyle = '#f6c453'; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(x, y - h * 0.75); ctx.lineTo(x, y - h * 0.5); ctx.stroke();
    // ฝาบน/ล่าง
    ctx.fillStyle = '#c9a227';
    ctx.fillRect(x - w * 0.28, y - h * 0.56, w * 0.56, h * 0.08);
    ctx.fillRect(x - w * 0.28, y + h * 0.48, w * 0.56, h * 0.08);
    // ตัวโคม
    const g = ctx.createRadialGradient(x - w * 0.2, y - h * 0.15, w * 0.1, x, y, w * 0.8);
    g.addColorStop(0, '#ff6b6b'); g.addColorStop(0.6, '#d11a1a'); g.addColorStop(1, '#7a0c0c');
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.ellipse(x, y, w / 2, h / 2, 0, 0, Math.PI * 2); ctx.fill();
    // เส้นซี่โคม
    ctx.strokeStyle = 'rgba(122,12,12,0.7)'; ctx.lineWidth = 1;
    [-0.3, 0, 0.3].forEach(k => { ctx.beginPath(); ctx.ellipse(x, y, Math.abs(w / 2 * (k === 0 ? 0.01 : k * 1.6)), h / 2, 0, 0, Math.PI * 2); ctx.stroke(); });
    // พู่
    ctx.strokeStyle = '#f6c453'; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(x, y + h * 0.56); ctx.lineTo(x, y + h * 0.78); ctx.stroke();
    ctx.lineWidth = 1;
    for (let i = -2; i <= 2; i++) { ctx.beginPath(); ctx.moveTo(x, y + h * 0.78); ctx.lineTo(x + i * 2.2, y + h * 1.0); ctx.stroke(); }
    ctx.restore();
}

function drawLanternHeader(ctx, W, headerH, fontFamily, hasAvatar = false) {
    // ไล่เงาทองจาง ๆ ด้านบน + ลายเมฆ
    const glow = ctx.createRadialGradient(W * 0.7, 10, 10, W * 0.7, 10, 220);
    glow.addColorStop(0, 'rgba(246,196,83,0.22)'); glow.addColorStop(1, 'rgba(246,196,83,0)');
    ctx.fillStyle = glow; ctx.fillRect(0, 0, W, headerH + 30);
    drawCloudPattern(ctx, W, headerH + 30);
    // แถบทองล่างหัว
    const ribbon = ctx.createLinearGradient(0, 0, W, 0);
    ribbon.addColorStop(0, '#7a5a12'); ribbon.addColorStop(0.5, '#ffe9a8'); ribbon.addColorStop(1, '#7a5a12');
    ctx.fillStyle = ribbon; ctx.fillRect(0, headerH + 24, W, 4);
    // โคมไฟแขวน
    if (hasAvatar) {
        drawLantern(ctx, W - 150, 74, 34);
        drawLantern(ctx, W - 196, 96, 24, 0.9);
    } else {
        drawLantern(ctx, W - 70, 78, 44);
        drawLantern(ctx, W - 130, 100, 30, 0.9);
        drawCoin(ctx, W - 40, 140, 12, 0.9);
    }
    drawCoin(ctx, W - 168, 142, 11, 0.85);
    drawSparkle(ctx, W - 104, 36, 5, 0.9);
    drawSparkle(ctx, W - 28, 52, 4, 0.8);
    drawSparkle(ctx, 250, 34, 4, 0.6);
    drawCoin(ctx, 22, headerH + 6, 30, 0.14);
}

// ---------- ลวดลายธีมทองคำ ----------
function drawGoldHeader(ctx, W, headerH, fontFamily, hasAvatar = false) {
    drawCloudPattern(ctx, W, headerH + 30);
    // แสงทองเรืองด้านบนขวา
    const glow = ctx.createRadialGradient(W - 80, 40, 10, W - 80, 40, 200);
    glow.addColorStop(0, 'rgba(255,243,196,0.35)'); glow.addColorStop(1, 'rgba(255,243,196,0)');
    ctx.fillStyle = glow; ctx.fillRect(0, 0, W, headerH + 30);
    // แถบทองเข้มล่างหัว
    const ribbon = ctx.createLinearGradient(0, 0, W, 0);
    ribbon.addColorStop(0, '#7a5a12'); ribbon.addColorStop(0.5, '#fff3c4'); ribbon.addColorStop(1, '#7a5a12');
    ctx.fillStyle = ribbon; ctx.fillRect(0, headerH + 24, W, 4);
    if (hasAvatar) {
        drawIngot(ctx, W - 150, 124, 50);
        drawCoin(ctx, W - 190, 132, 13, 0.95);
        drawCoin(ctx, W - 118, 142, 11, 0.95);
        drawCoin(ctx, W - 140, 56, 12, 0.85);
    } else {
        // กองทอง: ก้อนทองใหญ่ + ก้อนเล็ก + เหรียญซ้อนกัน
        drawIngot(ctx, W - 70, 118, 74);
        drawIngot(ctx, W - 126, 132, 46, 0.95);
        drawCoin(ctx, W - 40, 140, 14, 0.95);
        drawCoin(ctx, W - 100, 146, 11, 0.95);
        drawCoin(ctx, W - 160, 122, 14, 0.9);
        drawCoin(ctx, W - 56, 52, 16, 0.9);
        drawCoin(ctx, W - 112, 44, 11, 0.85);
        drawFuSeal(ctx, W - 168, 58, 18, fontFamily);
    }
    drawSparkle(ctx, W - 24, 30, 6, 0.95);
    drawSparkle(ctx, W - 84, 84, 5, 0.9);
    drawSparkle(ctx, W - 196, 100, 4, 0.8);
    drawSparkle(ctx, 262, 34, 4, 0.7);
    drawCoin(ctx, 22, headerH + 6, 30, 0.16);
}

// ---------- ลวดลายธีมดอกเหมย ----------
function drawPlumBlossom(ctx, x, y, r, alpha = 1) {
    ctx.save();
    ctx.globalAlpha = alpha;
    for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2 - Math.PI / 2;
        const px = x + Math.cos(a) * r * 0.62;
        const py = y + Math.sin(a) * r * 0.62;
        const g = ctx.createRadialGradient(px, py, r * 0.05, px, py, r * 0.5);
        g.addColorStop(0, '#ffffff'); g.addColorStop(0.5, '#ffd3de'); g.addColorStop(1, '#ff8fab');
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(px, py, r * 0.5, 0, Math.PI * 2); ctx.fill();
    }
    // เกสรทอง
    ctx.fillStyle = '#f6c453';
    ctx.beginPath(); ctx.arc(x, y, r * 0.22, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#b8860b'; ctx.lineWidth = 1;
    for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + Math.cos(a) * r * 0.38, y + Math.sin(a) * r * 0.38); ctx.stroke();
    }
    ctx.restore();
}

function drawPlumHeader(ctx, W, headerH, fontFamily, hasAvatar = false) {
    drawCloudPattern(ctx, W, headerH + 30);
    // แถบทองล่างหัว
    const ribbon = ctx.createLinearGradient(0, 0, W, 0);
    ribbon.addColorStop(0, '#7a5a12'); ribbon.addColorStop(0.5, '#ffe9a8'); ribbon.addColorStop(1, '#7a5a12');
    ctx.fillStyle = ribbon; ctx.fillRect(0, headerH + 24, W, 4);
    // กิ่งเหมย: โค้งจากมุมขวาบนลงมา
    const ox = hasAvatar ? -70 : 0;
    ctx.save();
    ctx.strokeStyle = '#3b1b10'; ctx.lineCap = 'round';
    ctx.lineWidth = 4;
    ctx.beginPath(); ctx.moveTo(W + 10 + ox, 6); ctx.bezierCurveTo(W - 60 + ox, 40, W - 120 + ox, 60, W - 180 + ox, 130); ctx.stroke();
    ctx.lineWidth = 2.5;
    ctx.beginPath(); ctx.moveTo(W - 80 + ox, 44); ctx.quadraticCurveTo(W - 70 + ox, 90, W - 40 + ox, 120); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(W - 130 + ox, 72); ctx.quadraticCurveTo(W - 150 + ox, 60, W - 170 + ox, 40); ctx.stroke();
    ctx.restore();
    // ดอกเหมย + ตูม
    drawPlumBlossom(ctx, W - 46 + ox, 28, 13);
    drawPlumBlossom(ctx, W - 96 + ox, 54, 15);
    drawPlumBlossom(ctx, W - 62 + ox, 96, 12, 0.95);
    drawPlumBlossom(ctx, W - 142 + ox, 88, 14);
    drawPlumBlossom(ctx, W - 176 + ox, 122, 11, 0.95);
    drawPlumBlossom(ctx, W - 164 + ox, 46, 9, 0.9);
    [[W - 120 + ox, 104], [W - 34 + ox, 124], [W - 186 + ox, 58]].forEach(([bx, by]) => {
        ctx.fillStyle = '#ff8fab'; ctx.beginPath(); ctx.arc(bx, by, 4, 0, Math.PI * 2); ctx.fill();
    });
    if (!hasAvatar) drawFuSeal(ctx, W - 40, 136, 16, fontFamily);
    drawSparkle(ctx, 262, 34, 4, 0.6);
    drawCoin(ctx, 22, headerH + 6, 30, 0.14);
}

// ---------- ลวดลายธีมประทัด ----------
function drawFirecrackerString(ctx, x, y, count, size = 10, alpha = 1) {
    ctx.save();
    ctx.globalAlpha = alpha;
    // เชือกแขวน
    ctx.strokeStyle = '#f6c453'; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(x, y - size); ctx.lineTo(x, y + count * size * 1.35); ctx.stroke();
    for (let i = 0; i < count; i++) {
        const cy = y + i * size * 1.35;
        const dx = (i % 2 === 0 ? -1 : 1) * size * 0.55;
        const g = ctx.createLinearGradient(x + dx - size * 0.5, 0, x + dx + size * 0.5, 0);
        g.addColorStop(0, '#7f0000'); g.addColorStop(0.5, '#ff3b3b'); g.addColorStop(1, '#9b0000');
        ctx.fillStyle = g;
        roundRect(ctx, x + dx - size * 0.5, cy, size, size * 1.1, size * 0.2); ctx.fill();
        ctx.fillStyle = '#f6c453';
        ctx.fillRect(x + dx - size * 0.5, cy, size, size * 0.16);
        ctx.fillRect(x + dx - size * 0.5, cy + size * 0.94, size, size * 0.16);
    }
    ctx.restore();
}

function drawBurst(ctx, x, y, r, color, alpha = 1) {
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = color; ctx.lineWidth = 1.5; ctx.lineCap = 'round';
    for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(x + Math.cos(a) * r * 0.35, y + Math.sin(a) * r * 0.35);
        ctx.lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r);
        ctx.stroke();
        ctx.fillStyle = color;
        ctx.beginPath(); ctx.arc(x + Math.cos(a) * r, y + Math.sin(a) * r, 1.6, 0, Math.PI * 2); ctx.fill();
    }
    ctx.restore();
}

function drawFirecrackerHeader(ctx, W, headerH, fontFamily, hasAvatar = false) {
    drawCloudPattern(ctx, W, headerH + 30);
    const ribbon = ctx.createLinearGradient(0, 0, W, 0);
    ribbon.addColorStop(0, '#7a5a12'); ribbon.addColorStop(0.5, '#ffe9a8'); ribbon.addColorStop(1, '#7a5a12');
    ctx.fillStyle = ribbon; ctx.fillRect(0, headerH + 24, W, 4);
    // พลุทองด้านบน
    drawBurst(ctx, W - 150, 34, 22, '#f6c453', 0.9);
    drawBurst(ctx, 270, 28, 14, '#ffe9a8', 0.7);
    drawBurst(ctx, W - 26, 70, 12, '#ffe9a8', 0.75);
    // พวงประทัด
    if (hasAvatar) {
        drawFirecrackerString(ctx, W - 150, 64, 5, 9);
        drawFirecrackerString(ctx, W - 190, 80, 4, 8, 0.9);
    } else {
        drawFirecrackerString(ctx, W - 60, 24, 8, 11);
        drawFirecrackerString(ctx, W - 110, 56, 6, 9, 0.95);
        drawFuSeal(ctx, W - 160, 120, 18, fontFamily);
    }
    drawCoin(ctx, W - 196, 136, 11, 0.85);
    drawSparkle(ctx, W - 98, 140, 5, 0.9);
    drawSparkle(ctx, W - 16, 150, 4, 0.8);
    drawCoin(ctx, 22, headerH + 6, 30, 0.14);
}

// ---------- ลวดลายธีม Neo ----------
function drawNeoHeader(ctx, W, headerH, hasAvatar = false) {
    const H = headerH + 30;
    ctx.save();
    // แถบทแยงเรืองแสง
    const stripes = [
        { x: W - 210, c: 'rgba(34,211,238,0.35)', w: 26 },
        { x: W - 160, c: 'rgba(168,85,247,0.35)', w: 14 },
        { x: W - 120, c: 'rgba(34,211,238,0.18)', w: 40 },
    ];
    if (!hasAvatar) stripes.push({ x: W - 60, c: 'rgba(168,85,247,0.25)', w: 18 });
    stripes.forEach(s => {
        ctx.fillStyle = s.c;
        ctx.beginPath();
        ctx.moveTo(s.x, 0); ctx.lineTo(s.x + s.w, 0); ctx.lineTo(s.x + s.w - 70, H); ctx.lineTo(s.x - 70, H);
        ctx.closePath(); ctx.fill();
    });
    // จุดกริด
    ctx.fillStyle = 'rgba(148,163,184,0.22)';
    for (let gx = 16; gx < W; gx += 22) for (let gy = 14; gy < H - 10; gy += 22) { ctx.beginPath(); ctx.arc(gx, gy, 0.9, 0, Math.PI * 2); ctx.fill(); }
    // เส้นนีออนใต้หัว
    const line = ctx.createLinearGradient(0, 0, W, 0);
    line.addColorStop(0, 'rgba(34,211,238,0)'); line.addColorStop(0.35, '#22d3ee'); line.addColorStop(0.7, '#a855f7'); line.addColorStop(1, 'rgba(168,85,247,0)');
    ctx.shadowColor = '#22d3ee'; ctx.shadowBlur = 12;
    ctx.fillStyle = line; ctx.fillRect(0, H - 3, W, 3);
    ctx.restore();
}

// ---------- ลวดลายธีมอั่งเปา ----------
function goldGradient(ctx, x, y, r) {
    const g = ctx.createRadialGradient(x - r * 0.35, y - r * 0.35, r * 0.1, x, y, r);
    g.addColorStop(0, '#fff3c4');
    g.addColorStop(0.45, '#f6c453');
    g.addColorStop(1, '#b8860b');
    return g;
}

// เหรียญจีนโบราณ (วงกลมเจาะรูสี่เหลี่ยม)
function drawCoin(ctx, x, y, r, alpha = 1) {
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = goldGradient(ctx, x, y, r);
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#8a5a00'; ctx.lineWidth = Math.max(1, r * 0.08);
    ctx.beginPath(); ctx.arc(x, y, r * 0.82, 0, Math.PI * 2); ctx.stroke();
    const h = r * 0.42;
    ctx.fillStyle = '#a31212';
    ctx.fillRect(x - h / 2, y - h / 2, h, h);
    ctx.strokeStyle = '#8a5a00'; ctx.lineWidth = Math.max(1, r * 0.06);
    ctx.strokeRect(x - h / 2, y - h / 2, h, h);
    ctx.restore();
}

// ก้อนทอง (元寶)
function drawIngot(ctx, x, y, w, alpha = 1) {
    const h = w * 0.55;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(x, y);
    // ตัวก้อน
    ctx.fillStyle = goldGradient(ctx, 0, 0, w * 0.6);
    ctx.beginPath();
    ctx.moveTo(-w / 2, 0);
    ctx.quadraticCurveTo(-w * 0.55, -h * 0.55, -w * 0.28, -h * 0.5);
    ctx.quadraticCurveTo(0, -h * 0.95, w * 0.28, -h * 0.5);
    ctx.quadraticCurveTo(w * 0.55, -h * 0.55, w / 2, 0);
    ctx.quadraticCurveTo(w * 0.35, h * 0.5, 0, h * 0.5);
    ctx.quadraticCurveTo(-w * 0.35, h * 0.5, -w / 2, 0);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#8a5a00'; ctx.lineWidth = Math.max(1, w * 0.03); ctx.stroke();
    // ยอดนูนตรงกลาง
    ctx.fillStyle = '#fff3c4';
    ctx.beginPath(); ctx.ellipse(0, -h * 0.38, w * 0.22, h * 0.18, 0, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#c9971c'; ctx.lineWidth = Math.max(1, w * 0.02); ctx.stroke();
    ctx.restore();
}

// ประกายดาว 4 แฉก
function drawSparkle(ctx, x, y, s, alpha = 1) {
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = '#fff3c4';
    ctx.beginPath();
    ctx.moveTo(x, y - s);
    ctx.quadraticCurveTo(x, y, x + s, y);
    ctx.quadraticCurveTo(x, y, x, y + s);
    ctx.quadraticCurveTo(x, y, x - s, y);
    ctx.quadraticCurveTo(x, y, x, y - s);
    ctx.fill();
    ctx.restore();
}

// ตราประทับ 福 วงกลมทอง
function drawFuSeal(ctx, x, y, r, fontFamily) {
    ctx.save();
    ctx.fillStyle = goldGradient(ctx, x, y, r);
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#8a5a00'; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.arc(x, y, r - 3, 0, Math.PI * 2); ctx.stroke();
    ctx.fillStyle = '#a31212';
    ctx.font = `700 ${Math.round(r * 1.15)}px "Noto Sans CJK SC", "Noto Sans SC", "PingFang SC", "Microsoft YaHei", "Hiragino Sans", ${fontFamily}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('福', x, y + r * 0.05);
    ctx.restore();
}

// ลายเมฆ/ลายกระหนกบาง ๆ บนพื้นหัว (เส้นโค้งซ้ำ)
function drawCloudPattern(ctx, W, H) {
    ctx.save();
    ctx.globalAlpha = 0.10;
    ctx.strokeStyle = '#ffd27a';
    ctx.lineWidth = 1.2;
    for (let y = 14; y < H; y += 26) {
        ctx.beginPath();
        for (let x = -20; x < W + 20; x += 40) {
            ctx.moveTo(x, y);
            ctx.bezierCurveTo(x + 10, y - 10, x + 30, y - 10, x + 40, y);
        }
        ctx.stroke();
    }
    ctx.restore();
}

// วาดของตกแต่งทั้งหมดของธีมอั่งเปาบนส่วนหัว
function drawAngpaoHeader(ctx, W, headerH, fontFamily, hasAvatar = false) {
    drawCloudPattern(ctx, W, headerH + 30);
    // แถบทองล่างหัว
    const ribbon = ctx.createLinearGradient(0, headerH + 22, W, headerH + 30);
    ribbon.addColorStop(0, '#b8860b'); ribbon.addColorStop(0.5, '#ffe9a8'); ribbon.addColorStop(1, '#b8860b');
    ctx.fillStyle = ribbon;
    ctx.fillRect(0, headerH + 24, W, 4);
    if (hasAvatar) {
        // มีรูปโปรไฟล์อยู่มุมขวา → ย้ายก้อนทอง/เหรียญไปข้างรูป ไม่ให้ซ้อน
        drawIngot(ctx, W - 150, 120, 52);
        drawCoin(ctx, W - 186, 136, 12, 0.9);
        drawCoin(ctx, W - 118, 140, 10, 0.9);
    } else {
        // ก้อนทอง + เหรียญ มุมขวา
        drawIngot(ctx, W - 78, 112, 72);
        drawCoin(ctx, W - 128, 128, 16);
        drawCoin(ctx, W - 40, 136, 13);
    }
    drawCoin(ctx, W - 150, 60, 11, 0.85);
    // ตรา 福
    drawFuSeal(ctx, hasAvatar ? W - 120 : W - 52, 44, hasAvatar ? 18 : 22, fontFamily);
    // ประกาย
    drawSparkle(ctx, W - 108, 36, 6, 0.9);
    drawSparkle(ctx, W - 20, 84, 5, 0.8);
    drawSparkle(ctx, W - 176, 104, 4, 0.7);
    drawSparkle(ctx, 250, 30, 4, 0.6);
    // เหรียญจาง ๆ มุมซ้ายล่าง
    drawCoin(ctx, 22, headerH + 6, 30, 0.18);
}

const OPTION_DEFS = [
    // รูปโปรไฟล์: แสดงเป็นตัวเลือกเฉพาะผู้ที่อัปโหลดรูปไว้ (requiresAvatar)
    { key: 'avatar', label: 'รูปโปรไฟล์ที่หัวสลิป', group: 'ข้อมูลส่วนตัว', def: true, requiresAvatar: true },
    { key: 'name', label: 'ชื่อ-นามสกุล', group: 'ข้อมูลส่วนตัว', def: true },
    // รายปีไม่มีข้อมูลตำแหน่ง/หน่วยงาน/บัญชี (monthlyOnly)
    { key: 'position', label: 'ตำแหน่ง', group: 'ข้อมูลส่วนตัว', def: true, monthlyOnly: true },
    { key: 'dept', label: 'หน่วยงาน', group: 'ข้อมูลส่วนตัว', def: false, monthlyOnly: true },
    { key: 'account', label: 'เลขที่บัญชี (ปิดบังบางส่วน)', group: 'ข้อมูลส่วนตัว', def: false, monthlyOnly: true },
    // กราฟรายเดือน มีเฉพาะแบบรายปี (yearlyOnly)
    { key: 'monthlyChart', label: 'กราฟรายได้สุทธิรายเดือน', group: 'ยอดเงิน', def: true, yearlyOnly: true },
    { key: 'net', label: 'ยอดเด่น (ตัวใหญ่ตรงกลาง)', group: 'ยอดเงิน', def: true },
    { key: 'totals', label: 'รวมรับ / รวมหัก', group: 'ยอดเงิน', def: true },
    { key: 'tax', label: 'ภาษี หัก ณ ที่จ่าย', group: 'ยอดเงิน', def: false },
    { key: 'gpf', label: 'กบข.', group: 'ยอดเงิน', def: false },
    { key: 'incomeList', label: 'รายการรับ (แยกรายการ)', group: 'รายละเอียด', def: false },
    { key: 'deductionList', label: 'รายการหัก (แยกรายการ)', group: 'รายละเอียด', def: false },
    { key: 'maskAmounts', label: 'ปิดบังตัวเลขทั้งหมด (แสดง •••••)', group: 'ความเป็นส่วนตัว', def: false },
];

// ยอดเด่นตัวใหญ่ตรงกลาง: รับสุทธิ / รายได้รวม / ทั้งสอง
const HERO_MODES = [
    { key: 'net', label: 'รับสุทธิ' },
    { key: 'gross', label: 'รายได้รวม' },
    { key: 'both', label: 'ทั้งสองยอด' },
];

const STORAGE_KEY = 'share_slip_options_v1';

function loadSavedOptions() {
    const defaults = Object.fromEntries(OPTION_DEFS.map(o => [o.key, o.def]));
    defaults.heroMode = 'net';
    try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
        if (saved && typeof saved === 'object') {
            return { ...defaults, ...saved.options, theme: saved.theme || DEFAULT_THEME_KEY };
        }
    } catch { /* ignore */ }
    return { ...defaults, theme: DEFAULT_THEME_KEY };
}

function formatNumber(num) {
    if (num === null || num === undefined) return '0.00';
    return Number(num).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function maskAccount(acc) {
    if (!acc) return '-';
    const s = String(acc);
    if (s.length <= 4) return s;
    return '•'.repeat(Math.max(0, s.length - 4)).replace(/(.{3})/g, '$1 ').trim() + ' ' + s.slice(-4);
}

function loadImage(src) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
    });
}

function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}

function ellipsize(ctx, text, maxWidth) {
    if (ctx.measureText(text).width <= maxWidth) return text;
    let t = text;
    while (t.length > 1 && ctx.measureText(t + '…').width > maxWidth) t = t.slice(0, -1);
    return t + '…';
}

/**
 * วาดสลิปลง canvas คืนค่า canvas
 */
async function renderSlip({ data, yyyy, mm, mode = 'monthly', options, theme, appName, fontFamily, logo, avatar }) {
    const { header, income, deduction, summary, monthly = [] } = data;
    const isYearly = mode === 'yearly';
    const titles = slipTitle(mode, yyyy, mm);
    const SCALE = 2;
    const W = 540;
    const PAD = 28;
    const INNER = W - PAD * 2;
    const money = (v) => options.maskAmounts ? '•••••' : formatNumber(v);

    const taxAmount = deduction.filter(i => i.code === '501').reduce((s, i) => s + Math.abs(i.amt || 0), 0);
    const gpfAmount = deduction.filter(i => i.code === '601' || i.code === '602').reduce((s, i) => s + Math.abs(i.amt || 0), 0);

    // ---- คำนวณความสูงก่อนวาด ----
    const ROW_H = 30;
    let H = 0;
    const headerH = 150;
    H += headerH + 22;
    // รายปีมีแค่ชื่อ (ไม่มีตำแหน่ง/หน่วยงาน/บัญชี)
    const showPosition = options.position && !isYearly;
    const showDept = options.dept && !isYearly;
    const showAccount = options.account && !isYearly;
    const hasPersonal = options.name || showPosition || showDept || showAccount;
    const personalLines = [options.name, showPosition, showDept, showAccount].filter(Boolean).length;
    const personalH = hasPersonal ? 26 + personalLines * 24 + 6 : 0;
    if (hasPersonal) H += personalH + 16;
    const heroMode = HERO_MODES.some(m => m.key === options.heroMode) ? options.heroMode : 'net';
    const netH = options.net ? (heroMode === 'both' ? 124 : 112) : 0;
    if (options.net) H += netH + 16;
    const totalsH = options.totals ? 86 : 0;
    if (options.totals) H += totalsH + 16;
    const showTax = options.tax;
    const showGpf = options.gpf && gpfAmount > 0;
    const taxGpfH = (showTax || showGpf) ? 64 : 0;
    if (taxGpfH) H += taxGpfH + 16;
    const showChart = isYearly && options.monthlyChart && monthly.length > 0;
    const chartH = showChart ? 180 : 0;
    if (showChart) H += chartH + 16;
    const incomeListH = options.incomeList ? 44 + income.length * ROW_H + ROW_H : 0;
    if (options.incomeList) H += incomeListH + 16;
    const dedListH = options.deductionList ? 44 + deduction.length * ROW_H + ROW_H : 0;
    if (options.deductionList) H += dedListH + 16;
    H += 64; // footer

    const canvas = document.createElement('canvas');
    canvas.width = W * SCALE;
    canvas.height = H * SCALE;
    const ctx = canvas.getContext('2d');
    ctx.scale(SCALE, SCALE);
    const font = (size, weight = 400) => `${weight} ${size}px ${fontFamily}`;

    const isChinese = !!theme.gold; // ธีมจีนมงคลทุกแบบ (อั่งเปา โคมแดง ทองคำ ดอกเหมย ประทัด)
    const P = { ...DEFAULT_PALETTE, ...theme };

    // พื้นหลัง
    ctx.fillStyle = theme.pageBg || '#f8fafc';
    ctx.fillRect(0, 0, W, H);

    // ---- ส่วนหัว gradient ----
    const grad = ctx.createLinearGradient(0, 0, W, headerH);
    grad.addColorStop(0, theme.from);
    grad.addColorStop(1, theme.to);
    ctx.fillStyle = grad;
    roundRect(ctx, 0, 0, W, headerH + 30, 0);
    ctx.fill();
    const showAvatar = !!(options.avatar && avatar);
    if (theme.decor === 'angpao') {
        drawAngpaoHeader(ctx, W, headerH, fontFamily, showAvatar);
    } else if (theme.decor === 'lantern') {
        drawLanternHeader(ctx, W, headerH, fontFamily, showAvatar);
    } else if (theme.decor === 'gold') {
        drawGoldHeader(ctx, W, headerH, fontFamily, showAvatar);
    } else if (theme.decor === 'plum') {
        drawPlumHeader(ctx, W, headerH, fontFamily, showAvatar);
    } else if (theme.decor === 'firecracker') {
        drawFirecrackerHeader(ctx, W, headerH, fontFamily, showAvatar);
    } else if (theme.decor === 'neo') {
        drawNeoHeader(ctx, W, headerH, showAvatar);
    } else if (theme.decor === 'clean') {
        // เส้นบางใต้หัว แบบมินิมอล
        ctx.fillStyle = '#d2d2d7';
        ctx.fillRect(0, headerH + 29, W, 1);
    } else {
        // วงกลมตกแต่ง
        ctx.globalAlpha = 0.12;
        ctx.fillStyle = '#ffffff';
        ctx.beginPath(); ctx.arc(W - 60, 30, 90, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(60, headerH + 10, 70, 0, Math.PI * 2); ctx.fill();
        ctx.globalAlpha = 1;
    }

    // โลโก้ + ชื่อแอป
    let x = PAD;
    if (logo) {
        ctx.save();
        roundRect(ctx, PAD, 26, 40, 40, 10);
        ctx.clip();
        ctx.drawImage(logo, PAD, 26, 40, 40);
        ctx.restore();
        x = PAD + 50;
    }
    ctx.fillStyle = P.headerText;
    ctx.font = font(15, 600);
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(appName || 'iMoney', x, 44);
    ctx.font = font(12, 400);
    ctx.fillStyle = P.headerSub;
    ctx.fillText('ระบบข้อมูลเงินเดือน', x, 62);

    ctx.fillStyle = P.titleColor;
    ctx.font = font(26, 700);
    ctx.fillText(titles.title, PAD, 112);
    ctx.font = font(16, 500);
    ctx.fillStyle = isChinese ? 'rgba(255,233,168,0.9)' : P.subtitleColor;
    ctx.fillText(titles.subtitle, PAD, 138);

    // ---- รูปโปรไฟล์ที่หัวสลิป (เฉพาะผู้ที่อัปโหลดรูปและเปิดตัวเลือกไว้) ----
    if (showAvatar) {
        const r = 34;
        const ax = W - PAD - r;
        const ay = 112;
        // วงแหวนรอบรูป
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.25)';
        ctx.shadowBlur = 10;
        ctx.shadowOffsetY = 3;
        ctx.fillStyle = isChinese ? goldGradient(ctx, ax, ay, r + 5) : 'rgba(255,255,255,0.95)';
        ctx.beginPath(); ctx.arc(ax, ay, r + 4, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
        // รูป (ครอปเป็นวงกลม แบบ cover)
        ctx.save();
        ctx.beginPath(); ctx.arc(ax, ay, r, 0, Math.PI * 2); ctx.clip();
        const iw = avatar.naturalWidth || avatar.width;
        const ih = avatar.naturalHeight || avatar.height;
        const s = Math.max((r * 2) / iw, (r * 2) / ih);
        const dw = iw * s, dh = ih * s;
        ctx.drawImage(avatar, ax - dw / 2, ay - dh / 2, dw, dh);
        ctx.restore();
        if (isChinese) {
            ctx.strokeStyle = '#8a5a00';
            ctx.lineWidth = 1;
            ctx.beginPath(); ctx.arc(ax, ay, r + 4, 0, Math.PI * 2); ctx.stroke();
        }
    }

    let y = headerH + 22;

    // การ์ดขาว helper (ธีมอั่งเปาใช้พื้นครีม + ขอบทอง)
    const card = (h) => {
        ctx.save();
        ctx.shadowColor = 'rgba(15,23,42,0.08)';
        ctx.shadowBlur = 12;
        ctx.shadowOffsetY = 4;
        ctx.fillStyle = theme.cardBg || '#ffffff';
        roundRect(ctx, PAD, y, INNER, h, 14);
        ctx.fill();
        ctx.restore();
        if (theme.cardBorder) {
            ctx.strokeStyle = theme.cardBorder;
            ctx.lineWidth = 1;
            roundRect(ctx, PAD, y, INNER, h, 14);
            ctx.stroke();
        }
    };

    // ---- ข้อมูลส่วนตัว ----
    if (hasPersonal) {
        card(personalH);
        let ly = y + 22;
        const line = (label, value) => {
            ctx.font = font(12, 400);
            ctx.fillStyle = P.mutedColor;
            ctx.fillText(label, PAD + 18, ly);
            ctx.font = font(14, 600);
            ctx.fillStyle = P.textColor;
            ctx.fillText(ellipsize(ctx, value, INNER - 36 - 96), PAD + 18 + 96, ly);
            ly += 24;
        };
        if (options.name) line('ชื่อ-นามสกุล', `${header.prename || ''}${header.fullname || ''}`);
        if (showPosition) line('ตำแหน่ง', header.positionname || '-');
        if (showDept) line('หน่วยงาน', `${header.depts || ''} ${header.dept_desc || ''}`.trim() || '-');
        if (showAccount) {
            const acc = `${header.pre_code || ''}${(header.emp_ac_no || '').replace(header.pre_code || '', '')}`;
            line('เลขที่บัญชี', maskAccount(acc));
        }
        y += personalH + 16;
    }

    // ---- รับสุทธิ (hero) ----
    if (options.net) {
        ctx.save();
        ctx.shadowColor = 'rgba(15,23,42,0.10)';
        ctx.shadowBlur = 14;
        ctx.shadowOffsetY = 5;
        const g2 = ctx.createLinearGradient(PAD, y, PAD + INNER, y + netH);
        g2.addColorStop(0, theme.soft);
        g2.addColorStop(1, theme.cardBg || '#ffffff');
        ctx.fillStyle = g2;
        roundRect(ctx, PAD, y, INNER, netH, 14);
        ctx.fill();
        ctx.restore();
        ctx.strokeStyle = isChinese ? theme.gold : (theme.heroBorder || theme.from);
        ctx.globalAlpha = isChinese ? 0.9 : (theme.heroBorderAlpha ?? 0.35);
        ctx.lineWidth = isChinese ? 2 : 1.5;
        roundRect(ctx, PAD, y, INNER, netH, 14);
        ctx.stroke();
        ctx.globalAlpha = 1;
        if (isChinese) {
            // เหรียญทองประดับมุมกล่องยอดเด่น
            drawCoin(ctx, PAD + 22, y + 22, 12, 0.9);
            drawCoin(ctx, PAD + INNER - 22, y + netH - 22, 12, 0.9);
            drawSparkle(ctx, PAD + INNER - 30, y + 18, 5, 0.9);
            drawSparkle(ctx, PAD + 34, y + netH - 16, 4, 0.8);
        }

        ctx.textAlign = 'center';
        if (heroMode === 'both') {
            // สองยอดเคียงกัน: รายได้รวม | รับสุทธิ (ไม่ใช้คำว่า "ก่อนหัก" บนสลิป)
            const half = INNER / 2;
            const col = (cx, label, value, color) => {
                ctx.font = font(13, 500);
                ctx.fillStyle = P.mutedColor;
                ctx.fillText(label, cx, y + 34);
                ctx.font = font(28, 700);
                ctx.fillStyle = color;
                ctx.fillText(money(value), cx, y + 76);
                ctx.font = font(12, 500);
                ctx.fillStyle = P.mutedColor;
                ctx.fillText('บาท', cx, y + 98);
            };
            col(PAD + half / 2, isYearly ? 'รายได้รวมทั้งปี' : 'รายได้รวม', summary.totalIncome, P.incomeColor);
            col(PAD + half + half / 2, isYearly ? 'รับสุทธิทั้งปี' : 'รับสุทธิ', summary.netPay, theme.accent);
            // เส้นคั่นกลาง
            ctx.strokeStyle = theme.from;
            ctx.globalAlpha = 0.3;
            ctx.beginPath(); ctx.moveTo(W / 2, y + 22); ctx.lineTo(W / 2, y + netH - 22); ctx.stroke();
            ctx.globalAlpha = 1;
        } else {
            const isGross = heroMode === 'gross';
            ctx.font = font(13, 500);
            ctx.fillStyle = P.mutedColor;
            ctx.fillText(
                isYearly ? (isGross ? 'รายได้รวมทั้งปี' : 'รับสุทธิรวมทั้งปี') : (isGross ? 'รายได้ประจำเดือน' : 'รับสุทธิ'),
                W / 2, y + 32
            );
            ctx.font = font(40, 700);
            ctx.fillStyle = theme.accent;
            ctx.fillText(money(isGross ? summary.totalIncome : summary.netPay), W / 2, y + 78);
            ctx.font = font(13, 500);
            ctx.fillStyle = P.mutedColor;
            ctx.fillText('บาท', W / 2, y + 98);
        }
        ctx.textAlign = 'left';
        y += netH + 16;
    }

    // ---- รวมรับ / รวมหัก ----
    if (options.totals) {
        const half = (INNER - 12) / 2;
        const box = (bx, label, value, color, bg) => {
            ctx.save();
            ctx.shadowColor = 'rgba(15,23,42,0.08)';
            ctx.shadowBlur = 12;
            ctx.shadowOffsetY = 4;
            ctx.fillStyle = bg;
            roundRect(ctx, bx, y, half, totalsH, 14);
            ctx.fill();
            ctx.restore();
            ctx.font = font(12, 500);
            ctx.fillStyle = P.mutedColor;
            ctx.fillText(label, bx + 16, y + 26);
            ctx.font = font(22, 700);
            ctx.fillStyle = color;
            ctx.fillText(money(value), bx + 16, y + 56);
            ctx.font = font(11, 400);
            ctx.fillStyle = P.mutedColor;
            ctx.fillText('บาท', bx + 16, y + 74);
        };
        box(PAD, isYearly ? 'รวมรับทั้งปี' : 'รวมรับ', summary.totalIncome, P.incomeColor, P.incomeBg);
        box(PAD + half + 12, isYearly ? 'รวมหักทั้งปี' : 'รวมหัก', summary.totalDeduction, P.deductionColor, P.deductionBg);
        y += totalsH + 16;
    }

    // ---- ภาษี / กบข. ----
    if (taxGpfH) {
        const items = [];
        if (showTax) items.push({ label: 'ภาษี หัก ณ ที่จ่าย', value: taxAmount });
        if (showGpf) items.push({ label: 'กบข.', value: gpfAmount });
        const w = (INNER - 12 * (items.length - 1)) / items.length;
        items.forEach((it, i) => {
            const bx = PAD + i * (w + 12);
            ctx.fillStyle = P.cardBg || '#ffffff';
            roundRect(ctx, bx, y, w, taxGpfH, 12);
            ctx.fill();
            ctx.strokeStyle = P.cardBorder || '#e2e8f0';
            ctx.lineWidth = 1;
            ctx.stroke();
            ctx.font = font(12, 400);
            ctx.fillStyle = P.mutedColor;
            ctx.fillText(it.label, bx + 14, y + 24);
            ctx.font = font(17, 600);
            ctx.fillStyle = P.textColor;
            ctx.fillText(`${money(it.value)} บาท`, bx + 14, y + 48);
        });
        y += taxGpfH + 16;
    }

    // ---- ตารางรายการ ----
    const list = (title, rows, total, color, h) => {
        card(h);
        ctx.font = font(14, 600);
        ctx.fillStyle = P.textColor;
        ctx.fillText(title, PAD + 18, y + 28);
        ctx.font = font(11, 400);
        ctx.fillStyle = P.mutedColor;
        ctx.textAlign = 'right';
        ctx.fillText(`${rows.length} รายการ`, PAD + INNER - 18, y + 28);
        ctx.textAlign = 'left';
        // เส้นคั่น
        ctx.strokeStyle = P.lineColor;
        ctx.beginPath(); ctx.moveTo(PAD + 18, y + 40); ctx.lineTo(PAD + INNER - 18, y + 40); ctx.stroke();
        let ry = y + 40 + ROW_H - 9;
        rows.forEach((r, i) => {
            if (i % 2 === 1) {
                ctx.fillStyle = theme.rowAlt || (isChinese ? '#fdf2e9' : '#f8fafc');
                ctx.fillRect(PAD + 10, ry - ROW_H + 9, INNER - 20, ROW_H);
            }
            ctx.font = font(12, 400);
            ctx.fillStyle = P.textColor;
            ctx.fillText(ellipsize(ctx, r.acc_name || `รหัส ${r.code}`, INNER - 36 - 120), PAD + 18, ry);
            ctx.textAlign = 'right';
            ctx.fillStyle = color;
            ctx.font = font(12, 500);
            ctx.fillText(money(Math.abs(r.amt || 0)), PAD + INNER - 18, ry);
            ctx.textAlign = 'left';
            ry += ROW_H;
        });
        ctx.strokeStyle = theme.totalLine || (isChinese ? '#e0c48a' : '#cbd5e1');
        ctx.beginPath(); ctx.moveTo(PAD + 18, ry - ROW_H + 9); ctx.lineTo(PAD + INNER - 18, ry - ROW_H + 9); ctx.stroke();
        ctx.font = font(13, 700);
        ctx.fillStyle = P.textColor;
        ctx.fillText('รวม', PAD + 18, ry + 2);
        ctx.textAlign = 'right';
        ctx.fillStyle = color;
        ctx.fillText(money(total), PAD + INNER - 18, ry + 2);
        ctx.textAlign = 'left';
        y += h + 16;
    };
    // ---- กราฟรายได้สุทธิรายเดือน (เฉพาะรายปี) ----
    if (showChart) {
        card(chartH);
        ctx.font = font(14, 600);
        ctx.fillStyle = P.textColor;
        ctx.fillText('รายได้สุทธิรายเดือน', PAD + 18, y + 28);
        const maxV = Math.max(0, ...monthly.map(m => Number(m.net) || 0));
        const left = PAD + 18;
        const slot = (INNER - 36) / 12;
        const barW = slot * 0.56;
        const top = y + 54;
        const baseY = y + chartH - 34;
        // เส้นฐาน
        ctx.strokeStyle = P.lineColor; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(left, baseY + 0.5); ctx.lineTo(PAD + INNER - 18, baseY + 0.5); ctx.stroke();
        for (let i = 1; i <= 12; i++) {
            const m = monthly.find(r => Number(r.mm) === i);
            const v = m ? (Number(m.net) || 0) : 0;
            const h = maxV > 0 ? Math.max(v > 0 ? 3 : 0, (v / maxV) * (baseY - top)) : 0;
            const bx = left + (i - 1) * slot + (slot - barW) / 2;
            if (h > 0) {
                const bg = ctx.createLinearGradient(0, baseY - h, 0, baseY);
                bg.addColorStop(0, isChinese ? theme.gold : theme.accent);
                bg.addColorStop(1, P.incomeColor);
                ctx.fillStyle = bg;
                roundRect(ctx, bx, baseY - h, barW, h, 3);
                ctx.fill();
                if (!options.maskAmounts && v > 0) {
                    ctx.font = font(8.5, 500);
                    ctx.fillStyle = P.mutedColor;
                    ctx.textAlign = 'center';
                    const k = v >= 1000 ? `${(v / 1000).toFixed(v >= 100000 ? 0 : 1)}k` : Math.round(v).toString();
                    ctx.fillText(k, bx + barW / 2, baseY - h - 4);
                    ctx.textAlign = 'left';
                }
            }
            ctx.font = font(9.5, 400);
            ctx.fillStyle = P.mutedColor;
            ctx.textAlign = 'center';
            ctx.fillText(thaiMonthsShort[i], bx + barW / 2, baseY + 15);
            ctx.textAlign = 'left';
        }
        y += chartH + 16;
    }

    if (options.incomeList) list(isYearly ? 'รายการรับ (รวมทั้งปี)' : 'รายการรับ', income, summary.totalIncome, P.incomeColor, incomeListH);
    if (options.deductionList) list(isYearly ? 'รายการหัก (รวมทั้งปี)' : 'รายการหัก', deduction, summary.totalDeduction, P.deductionColor, dedListH);

    // ---- ส่วนท้าย ----
    if (isChinese) {
        // เส้นทองคั่นท้าย + เหรียญเล็ก
        const fr = ctx.createLinearGradient(PAD, 0, PAD + INNER, 0);
        fr.addColorStop(0, 'rgba(184,134,11,0)'); fr.addColorStop(0.5, '#d4a017'); fr.addColorStop(1, 'rgba(184,134,11,0)');
        ctx.fillStyle = fr;
        ctx.fillRect(PAD, H - 52, INNER, 1.5);
        drawCoin(ctx, W / 2, H - 52, 7, 1);
    }
    const now = new Date();
    const stamp = `${now.getDate()} ${thaiMonths[now.getMonth() + 1]} ${now.getFullYear() + 543} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    ctx.font = font(11, 400);
    ctx.fillStyle = P.mutedColor;
    ctx.textAlign = 'center';
    ctx.fillText(`สร้างจาก ${appName || 'iMoney'} • ${stamp}`, W / 2, H - 34);
    ctx.fillText('ข้อมูลส่วนบุคคล โปรดระมัดระวังในการเผยแพร่', W / 2, H - 16);
    ctx.textAlign = 'left';

    return canvas;
}

/**
 * แปลงผลลัพธ์ /salary/yearly-summary ให้อยู่ในรูปแบบ data ของ ShareSlipModal (mode="yearly")
 * ใช้ร่วมกันทั้งหน้า YearlySummary และการ์ดรายรับรวมบน Dashboard
 */
export function buildYearlyShareData(yearly) {
    if (!yearly) return null;
    const { employee = {}, summary = {}, detailByType = [], monthlyDetail = [] } = yearly;
    const incomeItems = detailByType.filter(d => d.flag > 0);
    const deductionItems = detailByType.filter(d => d.flag < 0);
    return {
        header: { prename: '', fullname: employee.fullname || '' },
        income: incomeItems.map(i => ({ code: i.code, acc_name: i.acc_name, amt: i.total_amt })),
        deduction: deductionItems.map(i => ({ code: i.code, acc_name: i.acc_name, amt: i.total_amt })),
        summary: {
            totalIncome: summary.total_income || 0,
            totalDeduction: summary.total_deduction || 0,
            netPay: (summary.total_income || 0) - (summary.total_deduction || 0),
        },
        monthly: monthlyDetail.map(m => ({ mm: m.mm, net: m.net_pay ?? m.cnet ?? 0 })),
    };
}

function canvasToBlob(canvas) {
    return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
}

/**
 * props:
 * - mode: 'monthly' (ค่าเริ่มต้น) | 'yearly'
 * - data: monthly = { header, income, deduction, summary } จาก /salary/detail
 *         yearly  = รูปแบบเดียวกัน + monthly: [{ mm, net }] (หน้า YearlySummary แปลงให้)
 * - yyyy, mm (รายปีส่ง mm = 0)
 */
function ShareSlipModal({ data, yyyy, mm = 0, mode = 'monthly', onClose }) {
    const { appName } = useBranding();
    const isYearly = mode === 'yearly';
    const titles = slipTitle(mode, yyyy, mm);
    const { user } = useAuth();
    // URL รูปโปรไฟล์ (เสิร์ฟจาก /api เหมือนใน Layout) — ไม่มี = ไม่แสดงตัวเลือกรูปโปรไฟล์
    const avatarUrl = user?.avatar_url ? `/api${user.avatar_url}` : '';
    const avatarRef = useRef({ url: '', img: null });
    const saved = useMemo(loadSavedOptions, []);
    const [options, setOptions] = useState(() => {
        const { theme, ...rest } = saved;
        return rest;
    });
    const [themeKey, setThemeKey] = useState(saved.theme);
    const [previewUrl, setPreviewUrl] = useState('');
    const [rendering, setRendering] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const canvasRef = useRef(null);
    const logoRef = useRef(undefined);
    const bodyRef = useRef(null);
    const optionsRef = useRef(null);
    const [showScrollHint, setShowScrollHint] = useState(false);
    const hintDoneRef = useRef(false);

    // เมื่อเปิดครั้งแรก: ขยับแผงตัวเลือกขึ้น-ลงเบา ๆ + ป้ายบอก เพื่อสื่อว่าเลื่อนลงไปปรับแต่งสลิปได้
    useEffect(() => {
        if (!previewUrl || hintDoneRef.current) return;
        hintDoneRef.current = true;
        const body = bodyRef.current;
        const opts = optionsRef.current;
        const isScrollable = (el) => el && el.scrollHeight > el.clientHeight + 8;
        // มือถือ: body เป็นตัวเลื่อน / desktop: แผงตัวเลือกเป็นตัวเลื่อน
        const el = isScrollable(body) ? body : (isScrollable(opts) ? opts : null);
        if (!el) return;
        setShowScrollHint(true);
        const timers = [];
        const nudge = (to, delay) => timers.push(setTimeout(() => el.scrollTo({ top: to, behavior: 'smooth' }), delay));
        nudge(90, 400);
        nudge(0, 1000);
        nudge(60, 1700);
        nudge(0, 2200);
        timers.push(setTimeout(() => setShowScrollHint(false), 4500));
        const hide = () => setShowScrollHint(false);
        const t2 = setTimeout(() => el.addEventListener('scroll', hide, { once: true, passive: true }), 2400);
        timers.push(t2);
        return () => { timers.forEach(clearTimeout); el.removeEventListener('scroll', hide); };
    }, [previewUrl]);

    const theme = THEMES.find(t => t.key === themeKey) || THEMES.find(t => t.key === DEFAULT_THEME_KEY) || THEMES[0];
    const canWebShare = typeof navigator !== 'undefined' && !!navigator.share && !!navigator.canShare;

    // จำตัวเลือกล่าสุด
    useEffect(() => {
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ options, theme: themeKey })); } catch { /* ignore */ }
    }, [options, themeKey]);

    // วาดตัวอย่างทุกครั้งที่ตัวเลือกเปลี่ยน (debounce เล็กน้อย)
    useEffect(() => {
        let cancelled = false;
        const t = setTimeout(async () => {
            setRendering(true);
            try {
                if (logoRef.current === undefined) {
                    logoRef.current = await loadImage(`${process.env.PUBLIC_URL}/icon-192.png`);
                }
                if (document.fonts?.ready) { try { await document.fonts.ready; } catch { /* ignore */ } }
                // โหลดรูปโปรไฟล์ครั้งเดียวต่อ URL (โหลดเฉพาะเมื่อเปิดตัวเลือก)
                if (avatarUrl && options.avatar && avatarRef.current.url !== avatarUrl) {
                    avatarRef.current = { url: avatarUrl, img: await loadImage(avatarUrl) };
                }
                const fontFamily = getComputedStyle(document.body).fontFamily || 'sans-serif';
                const canvas = await renderSlip({
                    data, yyyy, mm, mode, options, theme, appName, fontFamily,
                    logo: logoRef.current,
                    avatar: avatarUrl ? avatarRef.current.img : null,
                });
                if (cancelled) return;
                canvasRef.current = canvas;
                setPreviewUrl(canvas.toDataURL('image/png'));
            } finally {
                if (!cancelled) setRendering(false);
            }
        }, 120);
        return () => { cancelled = true; clearTimeout(t); };
    }, [data, yyyy, mm, mode, options, theme, appName, avatarUrl]);

    const toggle = (key) => setOptions(o => ({ ...o, [key]: !o[key] }));

    const fileName = titles.file;

    // บันทึกประวัติการแชร์ให้ผู้ดูแลตรวจสอบได้ (best-effort ไม่บล็อกผู้ใช้)
    const logShare = useCallback((channel) => {
        const path = isYearly ? `/report/yearly/${yyyy}/share-log` : `/report/slip/${yyyy}/${mm}/share-log`;
        api.post(path, {
            channel,
            hero_mode: options.heroMode || 'net',
            theme: themeKey,
            options,
        }).catch(() => { /* ไม่กระทบการแชร์ */ });
    }, [isYearly, yyyy, mm, options, themeKey]);

    const download = useCallback(async () => {
        if (!canvasRef.current) return;
        const blob = await canvasToBlob(canvasRef.current);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
    }, [fileName]);

    // ปุ่ม "ดาวน์โหลดไฟล์" โดยตรง
    const downloadAndLog = useCallback(async () => {
        await download();
        logShare('download');
    }, [download, logShare]);

    const share = useCallback(async () => {
        if (!canvasRef.current) return;
        setSaving(true);
        setMessage('');
        try {
            const blob = await canvasToBlob(canvasRef.current);
            const file = new File([blob], fileName, { type: 'image/png' });
            if (canWebShare && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    files: [file],
                    title: titles.short,
                });
                logShare('save');
                setMessage('เปิดเมนูแชร์แล้ว เลือก "บันทึกรูปภาพ" เพื่อเก็บลงแกลเลอรี');
            } else {
                await download();
                logShare('download');
                setMessage('ดาวน์โหลดรูปภาพแล้ว');
            }
        } catch (err) {
            if (err?.name !== 'AbortError') {
                console.error('share error', err);
                try { await download(); logShare('download'); setMessage('ดาวน์โหลดรูปภาพแล้ว'); } catch { setMessage('ไม่สามารถบันทึกรูปได้'); }
            }
        } finally {
            setSaving(false);
        }
    }, [canWebShare, download, fileName, titles.short, logShare]);

    const groups = useMemo(() => {
        const g = [];
        OPTION_DEFS.forEach(o => {
            if (o.requiresAvatar && !avatarUrl) return; // ไม่มีรูปโปรไฟล์ → ไม่แสดงตัวเลือกนี้
            if (o.monthlyOnly && isYearly) return;
            if (o.yearlyOnly && !isYearly) return;
            let grp = g.find(x => x.name === o.group);
            if (!grp) { grp = { name: o.group, items: [] }; g.push(grp); }
            grp.items.push(o);
        });
        return g;
    }, [avatarUrl, isYearly]);

    return (
        <div className="share-modal-overlay" onClick={() => !saving && onClose()}>
            <div className="share-modal" onClick={(e) => e.stopPropagation()}>
                <button className="share-modal-close" onClick={onClose} disabled={saving} aria-label="ปิด">&times;</button>
                <h3>
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                    </svg>
                    {isYearly ? `แชร์สรุปรายได้ปี ${parseInt(yyyy) + 543}` : 'แชร์สลิป'}
                </h3>
                <p className="share-modal-sub">เลือกสิ่งที่ต้องการแสดงในรูป แล้วกดบันทึก</p>

                <div className="share-modal-body" ref={bodyRef}>
                    {/* ตัวเลือก */}
                    <div className="share-options" ref={optionsRef}>
                        <div className="share-group">
                            <div className="share-group-title">สีธีม</div>
                            <div className="share-themes">
                                {THEME_LIST.map(t => (
                                    <button
                                        key={t.key}
                                        type="button"
                                        className={`share-theme ${t.key === themeKey ? 'active' : ''} ${t.decor ? `decor-${t.decor}` : ''}`}
                                        style={{ background: t.swatch || `linear-gradient(135deg, ${t.from}, ${t.to})` }}
                                        onClick={() => setThemeKey(t.key)}
                                        title={t.name}
                                        aria-label={t.name}
                                    />
                                ))}
                            </div>
                        </div>
                        {groups.map(g => (
                            <div className="share-group" key={g.name}>
                                <div className="share-group-title">{g.name}</div>
                                {g.items.map(o => (
                                    <React.Fragment key={o.key}>
                                        <label className={`share-option ${o.key === 'maskAmounts' ? 'privacy' : ''}`}>
                                            <input type="checkbox" checked={!!options[o.key]} onChange={() => toggle(o.key)} />
                                            <span className="share-switch" aria-hidden="true" />
                                            <span className="share-option-label">{o.label}</span>
                                        </label>
                                        {/* เลือกยอดที่จะโชว์เป็นตัวใหญ่ */}
                                        {o.key === 'net' && options.net && (
                                            <div className="share-segment" role="radiogroup" aria-label="ยอดเด่น">
                                                {HERO_MODES.map(m => (
                                                    <button
                                                        key={m.key}
                                                        type="button"
                                                        role="radio"
                                                        aria-checked={(options.heroMode || 'net') === m.key}
                                                        className={`share-segment-btn ${(options.heroMode || 'net') === m.key ? 'active' : ''}`}
                                                        onClick={() => setOptions(prev => ({ ...prev, heroMode: m.key }))}
                                                    >
                                                        {m.label}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                        ))}
                    </div>

                    {/* ตัวอย่าง */}
                    <div className="share-preview">
                        <div className="share-preview-frame">
                            {previewUrl ? (
                                <img src={previewUrl} alt="ตัวอย่างสลิป" />
                            ) : (
                                <div className="share-preview-loading"><div className="spinner" /></div>
                            )}
                            {rendering && previewUrl && <div className="share-preview-updating">กำลังอัปเดต…</div>}
                        </div>
                        <div className="share-preview-hint">
                            บนมือถือ สามารถกดค้างที่รูปเพื่อบันทึกได้เช่นกัน
                        </div>
                    </div>
                </div>

                {showScrollHint && (
                    <div className="share-scroll-hint" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 12 15 18 9"/>
                        </svg>
                        เลื่อนลงเพื่อปรับแต่งสลิป
                    </div>
                )}

                <div className="share-actions">
                    <button className="share-btn primary" onClick={share} disabled={saving || rendering || !previewUrl}>
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        {saving ? 'กำลังบันทึก…' : (canWebShare ? 'บันทึก / แชร์รูปภาพ' : 'ดาวน์โหลดรูปภาพ')}
                    </button>
                    {canWebShare && (
                        <button className="share-btn secondary" onClick={downloadAndLog} disabled={saving || rendering || !previewUrl}>
                            ดาวน์โหลดไฟล์
                        </button>
                    )}
                </div>
                {message && <div className="share-message">{message}</div>}
            </div>
        </div>
    );
}

export default ShareSlipModal;
